
import { computed, defineComponent, onMounted, ref, getCurrentInstance, watch } from "vue-demi";
import axios from "@/plugins/http";
import { API_ROUTES } from "@/utils/API";
import { SMCases, SMQuestionnaire, SMUser } from "@/store";
import {
  API_TCandidates,
  TCandidate,
  convertCandidateForTable,
  buttonModel,
} from "@/models/candidate";

import { SL_ONBOARDING, NO_SL, SL, SL_IN_PROGRESS, SL_ON_REVIEW } from "@/constants/banner";

export default defineComponent({
  name: "VEmployerVisaCasesBrowse",

  components: {
    Banner: () => import("@/components/V2/Banner/Banner.vue"),
    TableBanner: () => import("./components/TableBanner.vue"),
    ArchiveUnavailablePopup: () => import("@/components/Popups/ArchiveUnavailablePopup.vue"),
  },

  setup() {
    const router = getCurrentInstance()?.proxy?.$router;

    const bannerType = computed(() => {
      // SL obtained
      if (SMUser.isSponsor && SMUser.isSponsorConfirmation) return SL;

      // Onboarding, SL true
      if (SMUser.isSponsor && !SMUser.isSponsorConfirmation) return SL_ONBOARDING;

      // SL in progress
      if (!SMUser.isSponsor && SMUser.isSLStarted) return SL_IN_PROGRESS;

      // No SL
      if (!SMUser.isSponsor) return NO_SL;

      // SL on review - ?
      if (SMUser.slIsNotOnFirstStep) return SL_ON_REVIEW;
    });

    async function onNameClick(item: TCandidate) {
      if (!item.user_id) return;

      await router.push(`/employer/visa-cases/${item.user_id}`);
    }

    async function onServiceClick(item: TCandidate) {
      if (!item.case_id) return;

      await router.push(`/cases/${item.case_id}/dashboard/${SMCases.tabId}`);
    }

    const activeTab = ref("active");

    function onTabSelect(tab: string) {
      if (!tab || tab === activeTab.value) return;

      activeTab.value = tab;
    }

    const headers = computed(() =>
      activeTab.value == "active"
        ? [
            {
              label: "Name",
              value: "name",
              span: 2,
              sort: true,
              sortBy: "text",
              action: onNameClick,
            },
            {
              label: "Job position",
              value: "position",
            },
            {
              label: "Service",
              value: "service",
              action: onServiceClick,
            },
            {
              label: "Status",
              value: "status",
              filter: true,
            },
            {
              label: "Action Required",
              value: "actions",
            },
          ]
        : [
            {
              label: "Name",
              value: "name",
              span: 2,
              sort: true,
              sortBy: "text",
              action: onNameClick,
            },
            {
              label: "Job position",
              value: "position",
            },
            {
              label: "Service",
              value: "service",
              action: onServiceClick,
            },
            {
              label: "Status",
              value: "status",
              filter: true,
            },
            {
              label: "Action",
              value: "actions",
            },
          ]
    );

    const searchBy = ["name", "position"];

    const dataForTabs = ref<Record<string, TCandidate[]>>({});

    const tabs = computed(() => [
      { title: "Active", value: "active", count: dataForTabs.value.active?.length || 0 },
      { title: "Archived", value: "archive", count: dataForTabs.value.archive?.length || 0 },
    ]);

    const hasNoData = computed(() => {
      return !dataForTabs.value.active?.length && !dataForTabs.value.archive?.length;
    });

    const loading = ref(false);

    async function fetchData() {
      try {
        loading.value = true;

        const { data } = await axios.get<API_TCandidates>(API_ROUTES.CANDIDATE());

        if (!data) return;

        dataForTabs.value = Object.fromEntries(
          Object.entries(data).map(([key, list]) => [key, list.map(convertCandidateForTable)])
        );
      } finally {
        loading.value = false;
      }
    }

    const unarchivePopup = ref(false);

    function openUnArchivePopup() {
      unarchivePopup.value = true;
    }

    function closeUnArchivePopup() {
      unarchivePopup.value = false;
    }

    const unarchiveLoading = ref(false);

    const unarchiveEndpoint = (caseId: number) => {
      if (!caseId) return "";

      if (SMUser.isExpert) return API_ROUTES.EXPERT_CASE_UNARCHIVE(caseId);

      if (SMUser.isEmployer) return API_ROUTES.CASE_UNARCHIVE(caseId);

      return "";
    };

    async function unarchiveCandidate(caseId: number) {
      try {
        unarchiveLoading.value = true;

        await axios.get(unarchiveEndpoint(caseId));

        SMCases.setIsArchived(false);

        fetchData();
      } catch (e) {
        openUnArchivePopup();
      } finally {
        unarchiveLoading.value = false;
      }
    }

    async function onTableAction(item: TCandidate) {
      if (item.is_archived) {
        unarchiveCandidate(item.case_id);

        return;
      }

      if (item.tasks_to_do) {
        if (!item.case_id) return;

        await router.push(`/cases/${item.case_id}/dashboard/${SMCases.tabId}`);

        return;
      }

      if (item.service === "Eligibility Check") {
        await router.push(
          `/cases/${item.case_id}/report/1?report_template_alias=ca_report_candidate_assessment&name=eligibility_report`
        );
      }
    }

    onMounted(() => fetchData());

    const button = buttonModel;

    const needsUpdate = computed(() => SMQuestionnaire.isOpen);

    watch(
      needsUpdate,
      (value) => {
        if (!value) {
          SMUser.updateCurrentUser();
        }
      },
      { immediate: true }
    );

    return {
      bannerType,
      headers,
      tabs,
      dataForTabs,
      searchBy,
      hasNoData,
      loading,
      button,
      onTableAction,
      onTabSelect,
      unarchivePopup,
      closeUnArchivePopup,
    };
  },
});
